import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Paperclip, ChevronRight } from "lucide-react";
import AttachmentsModal from "./AttachmentsModal";
import Card from "../common/Card";
import Badge from "../common/Badge";
import { color, radius } from "../../styles/theme";

const STATUS_VARIANT = {
  Submitted: "warning",
  AwaitingPromoter: "primary",
  PromoterResponded: "info",
  UnderAdminReview: "neutral",
  Resolved: "success",
  Rejected: "warning",
  Cancelled: "danger",
};

/**
 * One dispute — ported from mobile's DisputeCell
 * (src/Screens/dispute-list/dispute-cell). See the approved plan for the
 * deliberate Claim Reason / Your Response split (mobile's own field-picking
 * condition looks inverted for a promoter-only view).
 */
const DisputeCard = ({ item }) => {
  const { t } = useTranslation(["disputes", "common"]);
  const navigate = useNavigate();
  const [showAttachments, setShowAttachments] = useState(false);
  const attachments = (item?.Attachments || []).filter((a) => a?.FileUrl);

  return (
    <Card style={s.card}>
      <div style={s.headerRow}>
        <div style={s.eventRow}>
          {item?.EventThumbnail && <img src={item.EventThumbnail} alt="" style={s.thumb} />}
          <span style={s.eventName}>{item?.EventName}</span>
        </div>
        <Badge variant={STATUS_VARIANT[item?.Status] || "primary"}>{item?.Status}</Badge>
      </div>

      <Row label={t("disputeCard.gallery")} value={`${item?.GalleryName || ""}${item?.AlbumName ? ` / ${item.AlbumName}` : ""}`} />
      <Row label={t("disputeCard.claimType")} value={item?.ClaimTypeTitle} />
      {item?.RefundAmount != null && <Row label={t("disputeCard.refundAmount")} value={item.RefundAmount} />}
      {item?.ClaimDescription && <Row label={t("disputeCard.claimReason")} value={item.ClaimDescription} multiline />}
      {item?.PromoterResponse && <Row label={t("disputeCard.yourResponse")} value={item.PromoterResponse} multiline />}

      <div style={s.footer}>
        {attachments.length > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => setShowAttachments(true)}>
            <Paperclip size={13} strokeWidth={2.25} /> {t("disputeCard.attachments", { count: attachments.length })}
          </button>
        )}
        {item?.Status === "Submitted" && (
          <button type="button" className="btn btn-sm" style={s.respondBtn} onClick={() => navigate(`/disputes/${item.ClaimId}/respond`)}>
            {t("disputeCard.respond")} <ChevronRight size={13} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {showAttachments && <AttachmentsModal attachments={attachments} onClose={() => setShowAttachments(false)} />}
    </Card>
  );
};

const Row = ({ label, value, multiline = false }) => {
  if (multiline) {
    return (
      <div style={s.rowMultiline}>
        <div style={s.label}>{label}:</div>
        <div style={s.multilineValue}>{value}</div>
      </div>
    );
  }
  return (
    <div style={s.row}>
      <span style={s.label}>{label}:</span>
      <span style={s.value}>{value}</span>
    </div>
  );
};

const s = {
  card: { padding: 18, marginBottom: 16 },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  eventRow: { display: "flex", alignItems: "center", gap: 10 },
  thumb: { width: 30, height: 30, borderRadius: 15, objectFit: "cover" },
  eventName: { fontWeight: 700, fontSize: 14, color: color.primary600 },
  row: { display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8, gap: 12 },
  rowMultiline: { fontSize: 13, marginBottom: 8 },
  label: { color: color.textMuted, fontWeight: 600, flexShrink: 0 },
  value: { color: color.gray800, textAlign: "right" },
  multilineValue: { color: color.gray800, marginTop: 4, whiteSpace: "pre-wrap", lineHeight: 1.5 },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, borderTop: `1px solid ${color.gray100}`, paddingTop: 12 },
  linkBtn: { color: color.primary600, padding: "4px 0" },
  respondBtn: { marginLeft: "auto", background: color.warning500, color: "#fff", borderRadius: radius.sm },
};

export default DisputeCard;
